// Exercise Category enum matching backend
export enum ExerciseCategory {
  Warmup = 1,
  Footwork = 2,
  Abdominal = 3,
  HipWork = 4,
  Stretching = 5,
  ArmWork = 6,
  BackExtension = 7,
  Coordination = 8,
  Balance = 9,
  Jumping = 10,
  CoolDown = 11
}

// Difficulty Level enum matching backend
export enum DifficultyLevel {
  Beginner = 1,
  Intermediate = 2,
  Advanced = 3,
  Expert = 4
}

// Helper functions for display names
export const getExerciseCategoryName = (category: ExerciseCategory): string => {
  switch (category) {
    case ExerciseCategory.Warmup:
      return 'Warmup';
    case ExerciseCategory.Footwork:
      return 'Footwork';
    case ExerciseCategory.Abdominal:
      return 'Abdominal';
    case ExerciseCategory.HipWork:
      return 'Hip Work';
    case ExerciseCategory.Stretching:
      return 'Stretching';
    case ExerciseCategory.ArmWork:
      return 'Arm Work';
    case ExerciseCategory.BackExtension:
      return 'Back Extension';
    case ExerciseCategory.Coordination:
      return 'Coordination';
    case ExerciseCategory.Balance:
      return 'Balance';
    case ExerciseCategory.Jumping:
      return 'Jumping';
    case ExerciseCategory.CoolDown:
      return 'Cool Down';
    default:
      return 'Unknown';
  }
};

export const getDifficultyLevelName = (difficulty: DifficultyLevel): string => {
  switch (difficulty) {
    case DifficultyLevel.Beginner:
      return 'Beginner';
    case DifficultyLevel.Intermediate:
      return 'Intermediate';
    case DifficultyLevel.Advanced:
      return 'Advanced';
    case DifficultyLevel.Expert:
      return 'Expert';
    default:
      return 'Unknown';
  }
};

// API Request/Response Types
export interface CreatePlatformExerciseRequest {
  name: string;
  description?: string;
  instructions?: string;
  category: ExerciseCategory;
  difficulty: DifficultyLevel;
  primaryEquipmentId: number;
  typicalDurationSeconds?: number;
  defaultReps?: number;
  defaultSpringSettings?: string;
  commonPrimarySetups?: string;
  displayOrder: number;
  tags?: string;
  modalities?: string;
  alternativeEquipmentIds?: number[];
}

export interface UpdatePlatformExerciseRequest {
  name?: string;
  description?: string;
  instructions?: string;
  category: ExerciseCategory;
  difficulty: DifficultyLevel;
  primaryEquipmentId: number;
  typicalDurationSeconds?: number;
  defaultReps?: number;
  defaultSpringSettings?: string;
  commonPrimarySetups?: string;
  displayOrder: number;
  tags?: string;
  modalities?: string;
  isActive: boolean;
  alternativeEquipmentIds?: number[];
}

export interface AlternativeEquipmentResponse {
  equipmentId: number;
  equipmentName: string;
  isPreferred: boolean;
  modificationNotes?: string;
  alternativeSpringSettings?: string;
  difficultyAdjustment: number;
}

export interface PlatformExerciseSummaryResponse {
  id: number;
  name: string;
  description?: string;
  category: ExerciseCategory;
  difficulty: DifficultyLevel;
  primaryEquipmentId: number;
  primaryEquipmentName: string;
  typicalDurationSeconds?: number;
  defaultReps?: number;
  isActive: boolean;
  displayOrder: number;
  tags?: string;
  modalities?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface PlatformExerciseDetailResponse {
  id: number;
  name: string;
  description?: string;
  instructions?: string;
  category: ExerciseCategory;
  difficulty: DifficultyLevel;
  primaryEquipmentId: number;
  primaryEquipmentName: string;
  typicalDurationSeconds?: number;
  defaultReps?: number;
  defaultSpringSettings?: string;
  commonPrimarySetups?: string;
  displayOrder: number;
  isActive: boolean;
  tags?: string;
  modalities?: string;
  isPlatformExercise: boolean;
  alternativeEquipment: AlternativeEquipmentResponse[];
  createdAt: string;
  updatedAt?: string;
}

// Paginated API response
export interface PlatformExerciseListResponse {
  exercises: PlatformExerciseSummaryResponse[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

// Form data type for the exercise form
export interface ExerciseFormData {
  name: string;
  description: string;
  instructions: string;
  category: ExerciseCategory;
  difficulty: DifficultyLevel;
  primaryEquipmentId: number;
  typicalDurationSeconds?: number;
  defaultReps?: number;
  defaultSpringSettings: string;
  commonPrimarySetups: string;
  displayOrder: number;
  tags: string;
  modalities: string;
  isActive?: boolean;
  alternativeEquipmentIds: number[];
}

// Equipment option for dropdowns
export interface EquipmentOption {
  id: number;
  name: string;
  category: string;
  requiresSetup: boolean;
}